import React from 'react';
import {
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  TruckIcon,
  UserCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

export const formatCurrency = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return value;
  return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(numeric);
};

const toneStyles = {
  teal: {
    card: 'from-teal-500/12 to-cyan-500/8 border-teal-200/70 dark:border-teal-800/50',
    icon: 'bg-teal-500/10 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300',
    ring: 'ring-teal-400/40',
  },
  amber: {
    card: 'from-amber-500/12 to-orange-500/8 border-amber-200/70 dark:border-amber-800/50',
    icon: 'bg-amber-500/10 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
    ring: 'ring-amber-400/40',
  },
  rose: {
    card: 'from-rose-500/12 to-red-500/8 border-rose-200/70 dark:border-rose-800/50',
    icon: 'bg-rose-500/10 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',
    ring: 'ring-rose-400/40',
  },
  emerald: {
    card: 'from-emerald-500/12 to-green-500/8 border-emerald-200/70 dark:border-emerald-800/50',
    icon: 'bg-emerald-500/10 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
    ring: 'ring-emerald-400/40',
  },
  slate: {
    card: 'from-slate-500/8 to-gray-500/5 border-slate-200/70 dark:border-slate-700/60',
    icon: 'bg-slate-500/10 text-slate-700 dark:bg-slate-900/40 dark:text-slate-300',
    ring: 'ring-slate-400/40',
  },
  blue: {
    card: 'from-blue-500/12 to-indigo-500/8 border-blue-200/70 dark:border-blue-800/50',
    icon: 'bg-blue-500/10 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
    ring: 'ring-blue-400/40',
  },
};

const accentHero = {
  teal: 'from-teal-700 via-teal-600 to-cyan-500',
  blue: 'from-blue-700 via-sky-600 to-cyan-500',
  slate: 'from-slate-800 via-slate-700 to-slate-600',
};

export function VehiclePageShell({ children }) {
  return (
    <div className="min-h-screen bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-teal-50/40 via-slate-50 to-white dark:from-gray-950 dark:via-gray-900 dark:to-gray-950">
      <div className="space-y-2.5 px-3 py-2.5 sm:px-4 sm:py-3">{children}</div>
    </div>
  );
}

export function VehicleModuleHero({
  icon: Icon = TruckIcon,
  title,
  subtitle,
  accent = 'teal',
  actions = null,
}) {
  return (
    <div
      className={`relative overflow-hidden rounded-xl bg-gradient-to-br ${accentHero[accent] || accentHero.teal} px-4 py-3 text-white shadow-lg shadow-teal-900/15`}
    >
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(120deg,transparent_0%,rgba(255,255,255,0.08)_50%,transparent_100%)]" />
      <div className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full bg-white/10 blur-2xl" />
      <div className="relative flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/15 ring-1 ring-white/30 backdrop-blur-sm">
            <Icon className="h-[18px] w-[18px]" />
          </div>
          <div>
            <h1 className="text-base font-semibold tracking-tight sm:text-lg">{title}</h1>
            {subtitle ? <p className="mt-0.5 text-[11px] text-white/85 sm:text-xs">{subtitle}</p> : null}
          </div>
        </div>
        {actions ? <div className="flex flex-wrap items-center gap-1">{actions}</div> : null}
      </div>
    </div>
  );
}

export function VehicleStatGrid({ children }) {
  return <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">{children}</div>;
}

export function VehicleStatCard({
  label,
  value,
  tone = 'slate',
  icon: Icon,
  active = false,
  onClick = null,
}) {
  const styles = toneStyles[tone] || toneStyles.slate;
  const Tag = onClick ? 'button' : 'div';

  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick || undefined}
      className={`group rounded-xl border bg-gradient-to-br p-2.5 text-left shadow-sm transition-all duration-200 ${styles.card} ${
        onClick ? 'cursor-pointer hover:-translate-y-0.5 hover:shadow-md' : ''
      } ${active ? `ring-2 ${styles.ring}` : ''}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-[9px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
            {label}
          </p>
          <p className="mt-0.5 text-xl font-bold tabular-nums leading-none text-gray-900 dark:text-white">
            {value}
          </p>
        </div>
        {Icon ? (
          <div className={`rounded-lg p-1.5 transition-transform group-hover:scale-105 ${styles.icon}`}>
            <Icon className="h-4 w-4" />
          </div>
        ) : null}
      </div>
    </Tag>
  );
}

const formatPlateDisplay = (plate) => {
  if (!plate) return '—';
  const raw = String(plate).toUpperCase().replace(/[^A-Z0-9]/g, '');
  if (!raw) return '—';

  let index = 0;
  let city = '';
  while (index < raw.length && city.length < 2 && /\d/.test(raw[index])) {
    city += raw[index];
    index += 1;
  }

  let letters = '';
  while (index < raw.length && letters.length < 3 && /[A-Z]/.test(raw[index])) {
    letters += raw[index];
    index += 1;
  }

  const tail = raw.slice(index).replace(/\D/g, '').slice(0, 4);
  return [city, letters, tail].filter(Boolean).join(' ') || plate;
};

/** DataTable column props — keeps plate column narrow across vehicle module pages */
export const vehiclePlateColumnProps = {
  header: 'Plaka',
  accessorKey: 'license_plate',
  size: 136,
  minSize: 128,
  maxSize: 144,
  headerClassName: 'w-[1%] whitespace-nowrap !max-w-[8.5rem]',
  cellClassName: 'w-[1%] whitespace-nowrap align-top !max-w-[8.5rem] !break-normal',
};

export function VehiclePlateBadge({ plate, size = 'sm' }) {
  const display = formatPlateDisplay(plate);
  const isMd = size === 'md';

  return (
    <div
      className={`inline-flex w-fit max-w-full items-stretch overflow-hidden border-2 border-gray-800 bg-white shadow-sm dark:border-gray-500 ${
        isMd ? 'h-7 rounded-[3px]' : 'h-[17px] rounded-[2px]'
      }`}
    >
      <div
        className={`flex shrink-0 flex-col items-center justify-end bg-[#003399] text-white ${
          isMd ? 'w-6 px-0.5 pb-0.5 text-[6px]' : 'w-[14px] px-px pb-px text-[5px] leading-none'
        }`}
      >
        <span className="font-bold">TR</span>
      </div>
      <div
        className={`flex shrink-0 items-center whitespace-nowrap font-bold leading-none text-gray-900 ${
          isMd ? 'px-1.5 text-xs tracking-[0.08em]' : 'px-1 text-[8px] tracking-[0.04em]'
        }`}
      >
        {display}
      </div>
    </div>
  );
}

export function VehiclePlateCell({ plate, brand, model }) {
  const hasVehicleInfo = brand || model;

  return (
    <div className="inline-flex w-fit max-w-[8.5rem] flex-col gap-0.5">
      {hasVehicleInfo ? (
        <p className="max-w-[8.5rem] truncate text-[9px] leading-tight text-gray-600 dark:text-gray-400">
          {brand ? (
            <span className="font-semibold uppercase tracking-wide text-slate-700 dark:text-slate-300">
              {brand}
            </span>
          ) : null}
          {brand && model ? <span className="text-gray-400"> · </span> : null}
          {model ? <span>{model}</span> : null}
        </p>
      ) : null}
      <VehiclePlateBadge plate={plate} />
    </div>
  );
}

export function VehiclePersonCell({ name, subtitle }) {
  if (!name) {
    return <span className="text-gray-400">—</span>;
  }
  const initials = String(name)
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('');

  return (
    <div className="flex items-center gap-1.5">
      <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-teal-500 to-cyan-600 text-[9px] font-semibold text-white shadow-sm ring-2 ring-white dark:ring-gray-800">
        {initials || <UserCircleIcon className="h-3.5 w-3.5" />}
      </div>
      <div className="min-w-0">
        <p className="truncate text-xs font-medium text-gray-900 dark:text-white">{name}</p>
        {subtitle ? (
          <p className="truncate text-[10px] text-gray-500 dark:text-gray-400">{subtitle}</p>
        ) : null}
      </div>
    </div>
  );
}

const followUpMap = {
  active: {
    label: 'Devam Ediyor',
    className:
      'bg-emerald-100 text-emerald-800 ring-1 ring-emerald-200/80 dark:bg-emerald-900/30 dark:text-emerald-300 dark:ring-emerald-800/50',
    Icon: CheckCircleIcon,
  },
  approaching: {
    label: 'Süre Yaklaşıyor',
    className:
      'bg-amber-100 text-amber-900 ring-1 ring-amber-200/80 dark:bg-amber-900/30 dark:text-amber-300 dark:ring-amber-800/50',
    Icon: ClockIcon,
  },
  expired: {
    label: 'Süresi Doldu',
    className:
      'bg-rose-100 text-rose-800 ring-1 ring-rose-200/80 dark:bg-rose-900/30 dark:text-rose-300 dark:ring-rose-800/50',
    Icon: ExclamationTriangleIcon,
  },
  unknown: {
    label: '—',
    className:
      'bg-gray-100 text-gray-600 ring-1 ring-gray-200/80 dark:bg-gray-800 dark:text-gray-300 dark:ring-gray-700',
    Icon: null,
  },
};

export function StatusBadge({ status, label }) {
  const current = followUpMap[status] || followUpMap.unknown;
  const Icon = current.Icon;
  return (
    <span
      className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[9px] font-semibold leading-tight ${current.className}`}
    >
      {Icon ? <Icon className="h-2.5 w-2.5" /> : null}
      {label || current.label}
    </span>
  );
}

export function VehicleSelectionPreview({ vehicle, personLabel = 'Zimmetli', personName, onClear }) {
  if (!vehicle) return null;
  const vehicleId = vehicle.id;
  const links = vehicleId
    ? [
        { label: 'Sigorta', to: `/vehicle-maintenance/insurance?vehicle_id=${vehicleId}` },
        { label: 'Muayene', to: `/vehicle-maintenance/inspections?vehicle_id=${vehicleId}` },
        { label: 'Ceza', to: `/vehicle-maintenance/traffic-fines?vehicle_id=${vehicleId}` },
        { label: 'Bakım', to: `/vehicle-maintenance/maintenance?vehicle_id=${vehicleId}` },
      ]
    : [];

  return (
    <div className="rounded-xl border border-teal-200/80 bg-gradient-to-r from-teal-50/90 via-white to-cyan-50/40 p-3 shadow-sm dark:border-teal-900/40 dark:from-teal-950/30 dark:via-gray-900 dark:to-cyan-950/20">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-teal-600 to-cyan-600 text-white shadow-md">
            <TruckIcon className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-teal-700 dark:text-teal-300">
              Seçili araç
            </p>
            <div className="mt-1">
              <VehiclePlateBadge plate={vehicle.license_plate} size="md" />
            </div>
            <p className="mt-1 truncate text-xs text-gray-500 dark:text-gray-400">
              {[vehicle.brand, vehicle.model].filter(Boolean).join(' ') || vehicle.vehicle_code}
              {vehicle.city ? ` · ${vehicle.city}` : ''}
            </p>
          </div>
        </div>
        <div className="flex flex-col gap-2 sm:items-end">
          <VehiclePersonCell name={personName || vehicle.assigned_person} subtitle={personLabel} />
          <div className="flex flex-wrap gap-1">
            <StatusBadge status={vehicle.traffic_follow_up_status} label="Trafik" />
            <StatusBadge status={vehicle.insurance_follow_up_status} label="Sigorta" />
            <StatusBadge status={vehicle.inspection_follow_up_status} label="Muayene" />
          </div>
        </div>
      </div>
      {links.length ? (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {links.map((link) => (
            <a
              key={link.to}
              href={link.to}
              className="inline-flex items-center rounded-md border border-teal-200 bg-white px-2 py-1 text-[10px] font-semibold text-teal-800 shadow-sm hover:bg-teal-50 dark:border-teal-800 dark:bg-gray-900 dark:text-teal-200 dark:hover:bg-teal-950/40"
            >
              {link.label}
            </a>
          ))}
        </div>
      ) : null}
      {onClear ? (
        <button
          type="button"
          onClick={onClear}
          className="mt-2 inline-flex items-center gap-1 text-[10px] font-medium text-teal-700 hover:text-teal-900 dark:text-teal-300 dark:hover:text-teal-100"
        >
          <XMarkIcon className="h-3 w-3" />
          Seçimi kaldır
        </button>
      ) : null}
    </div>
  );
}

export function VehicleFormTabs({ tabs, activeTab, onChange }) {
  return (
    <div className="rounded-xl border border-gray-200/90 bg-gray-50/80 p-1 dark:border-gray-700/90 dark:bg-gray-800/40">
      <div className="grid gap-1" style={{ gridTemplateColumns: `repeat(${tabs.length}, minmax(0, 1fr))` }}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => onChange(tab.id)}
            className={`rounded-lg px-2 py-2 text-[10px] font-semibold transition-all sm:text-xs ${
              activeTab === tab.id
                ? 'bg-blue-600 text-white shadow-md'
                : 'text-gray-600 hover:bg-white/70 dark:text-gray-300 dark:hover:bg-gray-700/50'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export function VehicleFilterChips({ chips, onClearAll }) {
  if (!chips?.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-[10px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Aktif:</span>
      {chips.map((chip) => (
        <button
          key={chip.key}
          type="button"
          onClick={chip.onRemove}
          className="inline-flex items-center gap-1 rounded-full border border-teal-200 bg-teal-50 px-2 py-0.5 text-[10px] font-medium text-teal-800 transition hover:bg-teal-100 dark:border-teal-800 dark:bg-teal-950/40 dark:text-teal-200"
        >
          {chip.label}
          <XMarkIcon className="h-3 w-3" />
        </button>
      ))}
      {onClearAll ? (
        <button
          type="button"
          onClick={onClearAll}
          className="text-[10px] font-medium text-gray-500 underline-offset-2 hover:text-gray-700 hover:underline dark:text-gray-400"
        >
          Tümünü temizle
        </button>
      ) : null}
    </div>
  );
}

export function VehicleEmptyState({ icon: Icon = TruckIcon, title, description, action }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-200 bg-white/60 px-6 py-10 text-center dark:border-gray-700 dark:bg-gray-800/40">
      <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-teal-50 text-teal-600 dark:bg-teal-950/50 dark:text-teal-300">
        <Icon className="h-6 w-6" />
      </div>
      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{title}</h3>
      {description ? <p className="mt-1 max-w-sm text-xs text-gray-500 dark:text-gray-400">{description}</p> : null}
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  );
}

export function getVehicleById(vehicles, id) {
  if (!id) return null;
  return (vehicles || []).find((v) => String(v.id) === String(id)) || null;
}

export const vehicleFilterGridClass = 'grid grid-cols-1 gap-1.5';

export const vehicleFilterSectionClass = {
  teal: 'rounded-md border border-teal-100/80 bg-gradient-to-br from-white to-teal-50/40 p-1.5 dark:border-teal-900/40 dark:from-gray-800 dark:to-teal-950/20',
  slate: 'rounded-md border border-slate-200/80 bg-gradient-to-br from-white to-slate-50/60 p-1.5 dark:border-slate-700 dark:from-gray-800 dark:to-slate-900/40',
};

export const vehicleFilterSectionTitleClass = 'sr-only';

export const vehicleFilterFieldsGridClass = 'grid grid-cols-2 gap-1 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-7';

export const vehicleFilterFieldClass = 'rounded border border-gray-200 bg-white px-1 pb-0 pt-0 dark:border-gray-600 dark:bg-gray-700';

export const vehicleFilterLegendClass = 'px-0.5 text-[8px] text-gray-500 dark:text-gray-400 leading-none';

export const vehicleFilterInputClass = 'h-5 border-0 bg-transparent px-0 text-[10px]';

export const vehicleFilterSelectClass = 'h-5 w-full bg-transparent text-[10px] text-gray-900 outline-none dark:text-white';

export const vehicleFilterFooterClass = 'mt-1.5 flex justify-end border-t border-gray-100 pt-1.5 dark:border-gray-700';
